import { motion } from "framer-motion";
import { Icon } from "@iconify/react";

export default function HeroSection() {
    return (
        <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-linear-to-br from-slate-900 via-slate-800 to-blue-950">
            {/* Background decorations */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-blue-500/10 blur-3xl" />
                <div className="absolute -bottom-40 -left-40 w-[400px] h-[400px] rounded-full bg-blue-500/10 blur-3xl" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-blue-500/5 blur-3xl" />
                {/* Grid pattern */}
                <div
                    className="absolute inset-0 opacity-[0.03]"
                    style={{
                        backgroundImage: "linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)",
                        backgroundSize: "60px 60px",
                    }}
                />
            </div>

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    {/* Left - Text content */}
                    <motion.div
                        initial={{ opacity: 0, x: -40 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                    >
                        <div className="flex items-center gap-2 mb-6">
                            <div className="h-px w-8 bg-blue-500" />
                            <span className="text-blue-400 text-sm font-medium tracking-wider uppercase">
                                Software de Gestão Empresarial
                            </span>
                        </div>

                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                            Gerencie a sua empresa com{" "}
                            <span className="bg-linear-to-r from-blue-400 to-blue-300 bg-clip-text text-transparent">
                                inteligência
                            </span>
                        </h1>

                        <p className="text-lg text-white/60 mb-8 max-w-xl leading-relaxed">
                            O Logigate é um ERP completo e modular que integra facturação, recursos humanos,
                            finanças, gestão hospitalar e muito mais — tudo numa única plataforma.
                        </p>

                        <div className="flex flex-wrap gap-4 mb-12">
                            <a
                                href="#precos"
                                onClick={(e) => {
                                    e.preventDefault();
                                    document.querySelector("#precos")?.scrollIntoView({ behavior: "smooth" });
                                }}
                                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors"
                            >
                                <Icon icon="mdi:tag-outline" width={20} />
                                Ver Preços
                            </a>
                            <a
                                href="/criar"
                                className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/15 text-white font-semibold rounded-xl border border-white/20 transition-colors"
                            >
                                Começar grátis
                                <Icon icon="mdi:arrow-right" width={20} />
                            </a>
                        </div>

                        {/* Stats */}
                        <div className="flex flex-wrap gap-8">
                            {[
                                { value: "11+", label: "Módulos" },
                                { value: "100%", label: "Cloud" },
                                { value: "24/7", label: "Disponível" },
                            ].map((stat) => (
                                <div key={stat.label}>
                                    <div className="text-2xl font-bold text-white">{stat.value}</div>
                                    <div className="text-sm text-white/50">{stat.label}</div>
                                </div>
                            ))}
                        </div>
                    </motion.div>

                    {/* Right - Visual element */}
                    <motion.div
                        initial={{ opacity: 0, x: 40 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                        className="hidden lg:block"
                    >
                        <div className="relative">
                            {/* Dashboard mockup */}
                            <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 shadow-2xl">
                                <div className="flex items-center gap-2 mb-4">
                                    <div className="w-3 h-3 rounded-full bg-red-400" />
                                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                                    <div className="w-3 h-3 rounded-full bg-blue-400" />
                                    <div className="ml-auto text-xs text-white/30">logigate.hongayetu.com</div>
                                </div>

                                {/* Simulated dashboard */}
                                <div className="grid grid-cols-3 gap-3 mb-4">
                                    {[
                                        { icon: "mdi:receipt-text-outline", label: "Facturas", value: "1.243", color: "text-blue-400" },
                                        { icon: "mdi:account-group-outline", label: "Funcionários", value: "89", color: "text-blue-400" },
                                        { icon: "mdi:chart-line", label: "Receita", value: "+23%", color: "text-purple-400" },
                                    ].map((card) => (
                                        <div key={card.label} className="bg-white/5 rounded-xl p-3">
                                            <Icon icon={card.icon} width={20} className={card.color} />
                                            <div className="text-white font-semibold mt-1 text-sm">{card.value}</div>
                                            <div className="text-white/40 text-xs">{card.label}</div>
                                        </div>
                                    ))}
                                </div>

                                {/* Chart placeholder */}
                                <div className="bg-white/5 rounded-xl p-4">
                                    <div className="flex items-end gap-1.5 h-20">
                                        {[35, 55, 40, 70, 50, 80, 65, 90, 75, 85, 60, 95].map((h, i) => (
                                            <motion.div
                                                key={i}
                                                className="flex-1 rounded-t bg-linear-to-t from-blue-500/40 to-blue-400/60"
                                                initial={{ height: 0 }}
                                                animate={{ height: `${h}%` }}
                                                transition={{ duration: 0.5, delay: 0.5 + i * 0.05 }}
                                            />
                                        ))}
                                    </div>
                                    <div className="text-white/30 text-xs mt-2">Receita mensal (últimos 12 meses)</div>
                                </div>
                            </div>

                            {/* Floating badges */}
                            <motion.div
                                className="absolute -top-4 -right-4 bg-blue-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg"
                                animate={{ y: [0, -8, 0] }}
                                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                            >
                                ERP #1 em Angola
                            </motion.div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
